// Reusable UI components for ModelDataSampleList

import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    Menu,
    MenuItem,
    ListItemIcon,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    TablePagination,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SortIcon from '@mui/icons-material/Sort';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

import { Constants } from '../../../Models/Constants';
import { ColumnConfig } from './ModelDataSampleListTypes';
import { useModelDataSampleListStyles } from './ModelDataSampleListStyles';
import { CellFormatContext, formatCellValue, getCellColor } from './ModelDataSampleListUtils';

// Header component
interface HeaderComponentProps {
    title: string;
    productLine: string;
    onBackClick: () => void;
}

export const HeaderComponent: React.FC<HeaderComponentProps> = ({ title, productLine, onBackClick }) => {
    const styles = useModelDataSampleListStyles();
    
    return (
        <Box sx={styles.headerContainer}>
            <Button 
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                sx={styles.backButton}
                onClick={onBackClick}
            >
                Back
            </Button>
            
            <Box sx={styles.headerContent}>
                <Typography variant="h5" fontWeight="bold">
                    {title}
                </Typography>
                <Typography variant="subtitle1" sx={styles.productLine}>
                    {productLine}
                </Typography>
            </Box>
        </Box>
    );
};

// Filter controls component
interface FilterControlsProps {
    showFilters: boolean;
    isLoading: boolean;
    onToggleFilters: () => void;
}

export const FilterControls: React.FC<FilterControlsProps> = ({ 
    showFilters, 
    isLoading, 
    onToggleFilters 
}) => {
    const styles = useModelDataSampleListStyles();
    
    return (
        <Box sx={styles.filtersContainer}>
            <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                disabled={isLoading}
                onClick={onToggleFilters}
            >
                {showFilters ? 'Hide filters' : 'Show filters'}
            </Button>
        </Box>
    );
};

// Advanced filter component
interface AdvancedFilterProps {
    dataColumns: ColumnConfig[];
    filterColumn: string;
    filterValue: string;
    currentFilter: string;
    onColumnChange: (columnId: string) => void;
    onValueChange: (value: string) => void;
    onClear: () => void;
}

export const AdvancedFilter: React.FC<AdvancedFilterProps> = ({
    dataColumns,
    filterColumn,
    filterValue,
    currentFilter,
    onColumnChange,
    onValueChange,
    onClear
}) => {
    const styles = useModelDataSampleListStyles();
    
    return (
        <Box sx={styles.filterContainer}>
            <Typography variant="body2" sx={styles.filterLabel}>Filter by column:</Typography>
            <Box sx={styles.filterForm}>
                <Box sx={styles.filterControls}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="filter-column-label">Select column</InputLabel>
                        <Select
                            labelId="filter-column-label"
                            value={filterColumn}
                            onChange={(e) => onColumnChange(e.target.value)}
                            label="Select column"
                        >
                            <MenuItem value="">
                                <em>Select column</em>
                            </MenuItem>
                            {dataColumns.map(col => (
                                <MenuItem key={col.id} value={col.id}>{col.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <TextField 
                        size="small"
                        placeholder="Filter value" 
                        value={filterValue}
                        onChange={(e) => onValueChange(e.target.value)}
                        disabled={!filterColumn}
                        sx={{ minWidth: 200 }}
                    />
                    
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={onClear}
                        size="small"
                    >
                        Clear
                    </Button>
                </Box>
                
                {currentFilter && (
                    <Chip
                        icon={<WarningIcon />}
                        label="Filters are active"
                        color="warning"
                        variant="outlined"
                        sx={styles.warningBadge}
                        onDelete={onClear}
                    />
                )}
            </Box>
        </Box>
    );
};

// Loading component
export const LoadingComponent: React.FC = () => {
    const styles = useModelDataSampleListStyles();
    
    return (
        <Box sx={styles.loadingContainer}>
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ ml: 2 }}>Loading...</Typography>
        </Box>
    );
};

// No data component
export const NoDataComponent: React.FC = () => {
    const styles = useModelDataSampleListStyles();
    
    return (
        <Box sx={styles.noDataContainer}>
            <Typography variant="body1">No data samples found.</Typography>
        </Box>
    );
};

// Status badge component
interface StatusBadgeProps {
    status: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
    const styles = useModelDataSampleListStyles();
    
    if (status === Constants.DATASAMPLESTATUS_Done) {
        return (
            <Chip 
                label="Done" 
                sx={styles.statusDone}
                size="small"
            />
        );
    }
    if (status === Constants.DATASAMPLESTATUS_NeedsUpdate) {
        return (
            <Chip 
                label="Update" 
                sx={styles.statusUpdate}
                size="small"
            />
        );
    }
    return (
        <Chip 
            label="To do" 
            sx={styles.statusTodo}
            size="small"
        />
    );
};

// Comment icon component
interface CommentIconProps {
    comment: string;
}

export const CommentIcon: React.FC<CommentIconProps> = ({ comment }) => {
    const theme = useTheme();
    
    if (comment && comment !== '') {
        return (
            <Tooltip title={comment}>
                <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
                    <InfoIcon />
                </IconButton>
            </Tooltip>
        );
    }
    return null;
};

// Cell content component
interface CellContentProps {
    columnKey: string;
    value: any;
    formatContext: CellFormatContext;
}

export const CellContent: React.FC<CellContentProps> = ({ columnKey, value, formatContext }) => {
    const theme = useTheme();
    const output = formatCellValue(columnKey, value, formatContext);
    const color = getCellColor(columnKey, theme);

    return (
        <Tooltip title={output}>
            <Typography 
                variant="body2" 
                sx={{ color, cursor: 'pointer' }}
                noWrap
            >
                {output}
            </Typography>
        </Tooltip>
    );
};

// Table cell component
interface TableCellComponentProps {
    column: ColumnConfig;
    row?: any;
    isHeader?: boolean;
    onMenuClick?: (e: React.MouseEvent<HTMLElement>, columnId: string) => void;
    renderContent?: (column: ColumnConfig, row: any) => React.ReactNode;
}

export const TableCellComponent: React.FC<TableCellComponentProps> = ({ 
    column, 
    row, 
    isHeader = false, 
    onMenuClick,
    renderContent
}) => {
    const styles = useModelDataSampleListStyles();
    const { width } = column;
    const cellSx = isHeader ? styles.tableCellHeader : styles.tableCell;
    
    return (
        <TableCell 
            key={column.id}
            align={column.align}
            sx={{
                ...cellSx,
                width: `${width.minWidth}px`,
                '&.MuiTableCell-sizeMedium': {
                    ...cellSx['&.MuiTableCell-sizeMedium'],
                    width: `${width.minWidth}px`,
                    maxWidth: `${width.maxWidth}px`
                }
            }}
        >
            {isHeader ? (
                <Box sx={styles.columnHeaderContent}>
                    <Box sx={styles.headerLabelContainer}>
                        <Tooltip title={column.label} arrow>
                            <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                {column.label}
                            </Typography>
                        </Tooltip>
                    </Box>
                    {onMenuClick && (
                        <IconButton
                            size="small"
                            sx={styles.columnMenuButton}
                            onClick={(e) => onMenuClick(e, column.id)}
                            aria-label={`Menu for ${column.label}`}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            ) : (
                renderContent ? renderContent(column, row) : column.format((row as any)[column.id], row)
            )}
        </TableCell>
    );
};

// Data table component
interface DataTableProps {
    columns: ColumnConfig[];
    data: any[];
    onRowClick: (item: any) => void;
    onMenuClick: (e: React.MouseEvent<HTMLElement>, columnId: string) => void;
    renderCellContent: (column: ColumnConfig, row: any) => React.ReactNode;
}

export const DataTable: React.FC<DataTableProps> = ({
    columns,
    data,
    onRowClick,
    onMenuClick,
    renderCellContent
}) => {
    const styles = useModelDataSampleListStyles();
    
    return (
        <TableContainer component={Paper} elevation={3} sx={styles.tableContainer}>
            <Table stickyHeader aria-label="data sample table" sx={styles.stickyTable}>
                <TableHead>
                    <TableRow>
                        {columns.map(column => (
                            <TableCellComponent
                                key={column.id}
                                column={column}
                                isHeader={true}
                                onMenuClick={onMenuClick}
                            />
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map(row => (
                        <TableRow 
                            hover 
                            key={row.id}
                            onClick={() => onRowClick(row)}
                            sx={{ '&:hover': { cursor: 'pointer' } }}
                        >
                            {columns.map(column => (
                                <TableCellComponent
                                    key={column.id}
                                    column={column}
                                    row={row}
                                    renderContent={renderCellContent}
                                />
                            ))}
                        </TableRow>
                    ))}
                    {data.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={columns.length} align="center">
                                <Typography variant="body1" padding={3}>
                                    No matching records found
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// Column menu component
interface ColumnMenuProps {
    anchorEl: null | HTMLElement;
    open: boolean;
    onClose: () => void;
    onSort: (direction: 'asc' | 'desc' | null) => void;
    onHideColumn: () => void;
    onManageColumns: () => void;
}

export const ColumnMenu: React.FC<ColumnMenuProps> = ({
    anchorEl,
    open,
    onClose,
    onSort,
    onHideColumn,
    onManageColumns
}) => {
    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
        >
            <MenuItem onClick={() => onSort('asc')}>
                <ListItemIcon>
                    <ArrowUpwardIcon fontSize="small" />
                </ListItemIcon>
                Sort by ASC
            </MenuItem>
            <MenuItem onClick={() => onSort('desc')}>
                <ListItemIcon>
                    <ArrowDownwardIcon fontSize="small" />
                </ListItemIcon>
                Sort by DESC
            </MenuItem>
            <MenuItem onClick={() => onSort(null)}>
                <ListItemIcon>
                    <SortIcon fontSize="small" />
                </ListItemIcon>
                Unsort
            </MenuItem>
            <Divider />
            <MenuItem onClick={onHideColumn}>
                <ListItemIcon>
                    <VisibilityOffIcon fontSize="small" />
                </ListItemIcon>
                Hide column
            </MenuItem>
            <MenuItem onClick={onManageColumns}>
                <ListItemIcon>
                    <ViewColumnIcon fontSize="small" />
                </ListItemIcon>
                Manage columns
            </MenuItem>
        </Menu>
    );
};

// Column manager dialog component
interface ColumnManagerDialogProps {
    open: boolean;
    columns: ColumnConfig[];
    hiddenColumns: string[];
    onClose: () => void;
    onToggleColumn: (columnId: string) => void;
}

export const ColumnManagerDialog: React.FC<ColumnManagerDialogProps> = ({
    open,
    columns,
    hiddenColumns,
    onClose,
    onToggleColumn
}) => {
    const styles = useModelDataSampleListStyles();
    
    const isColumnVisible = (columnId: string) => !hiddenColumns.includes(columnId);
    
    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="column-manager-dialog-title"
        >
            <DialogTitle id="column-manager-dialog-title">
                Manage Columns
            </DialogTitle>
            <DialogContent dividers sx={styles.columnManagerDialog}>
                <List>
                    {columns.map(column => (
                        <ListItem key={column.id} dense disablePadding>
                            <ListItemButton onClick={() => onToggleColumn(column.id)}>
                                <Checkbox
                                    edge="start"
                                    checked={isColumnVisible(column.id)}
                                    tabIndex={-1}
                                    disableRipple
                                />
                                <ListItemText primary={column.label} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

// Pagination component wrapper
interface PaginationComponentProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const PaginationComponent: React.FC<PaginationComponentProps> = ({
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange
}) => {
    return (
        <TablePagination
            rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            labelRowsPerPage="Rows per page:"
        />
    );
}; 